import { useEffect, useState } from "react";
import { Plus, Trash2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { SectionTitle, Card } from "../ui";

const FIELD = "w-full bg-white border border-sand-200 rounded-lg px-3 py-2 text-sm text-charcoal-800 focus:outline-none focus:ring-2 focus:ring-sage/50";
const EMPTY = { name: "", photo: "", bio: "", languages: "English, Spanish", license_type: "", background_checked: true, years_experience: "", active: true, order: 0 };

const toPayload = (d) => ({
  ...d,
  languages: typeof d.languages === "string" ? d.languages.split(",").map((x) => x.trim()).filter(Boolean) : d.languages,
  years_experience: d.years_experience === "" || d.years_experience == null ? null : Number(d.years_experience),
  order: Number(d.order) || 0,
});

function DriverForm({ d, set, testPrefix }) {
  return (
    <div className="space-y-2">
      <div className="grid sm:grid-cols-2 gap-2">
        <input data-testid={`${testPrefix}-name`} className={FIELD} placeholder="Name" value={d.name} onChange={(e) => set("name", e.target.value)} />
        <input data-testid={`${testPrefix}-license`} className={FIELD} placeholder="License type (e.g. Class A CDL)" value={d.license_type} onChange={(e) => set("license_type", e.target.value)} />
      </div>
      <input data-testid={`${testPrefix}-photo`} className={FIELD} placeholder="Photo URL" value={d.photo} onChange={(e) => set("photo", e.target.value)} />
      <textarea data-testid={`${testPrefix}-bio`} rows={2} className={FIELD} placeholder="Short bio" value={d.bio} onChange={(e) => set("bio", e.target.value)} />
      <div className="grid sm:grid-cols-3 gap-2">
        <input data-testid={`${testPrefix}-languages`} className={FIELD} placeholder="Languages (comma separated)"
          value={Array.isArray(d.languages) ? d.languages.join(", ") : d.languages} onChange={(e) => set("languages", e.target.value)} />
        <input data-testid={`${testPrefix}-years`} type="number" min="0" className={FIELD} placeholder="Years of experience"
          value={d.years_experience ?? ""} onChange={(e) => set("years_experience", e.target.value)} />
        <input data-testid={`${testPrefix}-order`} type="number" className={FIELD} placeholder="Display order" value={d.order} onChange={(e) => set("order", e.target.value)} />
      </div>
      <div className="flex flex-wrap gap-4 text-sm text-charcoal-700">
        <label className="flex items-center gap-2">
          <input data-testid={`${testPrefix}-bgcheck`} type="checkbox" className="accent-terracotta" checked={!!d.background_checked} onChange={(e) => set("background_checked", e.target.checked)} />
          <ShieldCheck size={14} className="text-sage-700" /> Background-checked
        </label>
        <label className="flex items-center gap-2">
          <input data-testid={`${testPrefix}-active`} type="checkbox" className="accent-terracotta" checked={!!d.active} onChange={(e) => set("active", e.target.checked)} />
          Active (shown on the public page)
        </label>
      </div>
    </div>
  );
}

export default function DriversManager() {
  const [rows, setRows] = useState([]);
  const [neu, setNeu] = useState(EMPTY);

  const load = () => api.get("/admin/drivers").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!neu.name.trim()) return toast.error("Driver needs a name");
    try {
      await api.post("/admin/drivers", toPayload(neu));
      toast.success("Driver added");
      setNeu(EMPTY);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add driver"); }
  };

  const save = async (d) => {
    try {
      await api.put(`/admin/drivers/${d.id}`, toPayload(d));
      toast.success("Driver saved");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this driver profile?")) return;
    await api.delete(`/admin/drivers/${id}`);
    toast.success("Driver deleted");
    load();
  };

  const setField = (id, key, val) => setRows((arr) => arr.map((d) => (d.id === id ? { ...d, [key]: val } : d)));

  return (
    <div data-testid="admin-drivers">
      <SectionTitle title="Meet Our Drivers" subtitle="Public trust profiles shown at /drivers — photo, bio, badges. Assign a driver to a booking from Appointments and the client gets an intro email." />
      <Card className="mb-6">
        <h3 className="font-medium text-charcoal-800 mb-4">Add a driver</h3>
        <DriverForm d={neu} set={(k, v) => setNeu({ ...neu, [k]: v })} testPrefix="driver-new" />
        <button data-testid="driver-add" onClick={add} className="mt-3 inline-flex items-center gap-2 rounded-full bg-terracotta px-5 py-2 text-sm text-white font-medium hover:bg-terracotta-hover">
          <Plus size={16} /> Add driver
        </button>
      </Card>
      <div className="space-y-4">
        {rows.map((d) => (
          <Card key={d.id}>
            <div className="flex gap-4">
              {d.photo && <img src={d.photo} alt={d.name} className="h-24 w-24 shrink-0 rounded-xl object-cover" />}
              <div className="flex-1">
                <DriverForm d={d} set={(k, v) => setField(d.id, k, v)} testPrefix={`driver-${d.id}`} />
                <div className="mt-3 flex items-center gap-2">
                  <button data-testid={`driver-save-${d.id}`} onClick={() => save(d)} className="rounded-full bg-sage px-5 py-2 text-sm text-white font-medium hover:bg-sage-hover">Save</button>
                  <button data-testid={`driver-delete-${d.id}`} onClick={() => remove(d.id)} className="p-2 text-charcoal-400 hover:text-red-600"><Trash2 size={16} /></button>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
